import React, { useCallback } from 'react';

import { DispatcherDispatch } from '@admin/components/dispatcher/dispatcher_dispatch';
import {
  dispatchHasWarehouseOrder,
  moverCountErrors,
  vehicleRequestErrorMessages,
  vehicleRequestIconStatus,
} from '@admin/components/dispatcher/dispatcher_dispatch_validation';
import { DispatcherDropZone } from '@admin/components/dispatcher/dispatcher_drop_zone';
import { isRouteEditable } from '@admin/components/dispatcher/dispatcher_editor_utils';
import {
  DispatcherDispatchRecord,
  DispatcherLayout,
  DispatcherOrderRecord,
  FieldShiftAssignmentRecord,
} from '@admin/components/dispatcher/dispatcher_types';
import { DispatcherResources } from '@admin/components/dispatcher/hooks/use_dispatcher_resources';
import { ALL_MAP_COLORS } from '@admin/helpers/colors';

type DispatcherRouteColumnsProps = {
  layout: DispatcherLayout;
  resources: DispatcherResources;
  region: { id: number; tz: string };
  date: string;
  mode?: 'viewing' | 'modifying';
  fieldShiftAssignments: FieldShiftAssignmentRecord[];
  onRemove?: (dispatch: DispatcherDispatchRecord) => void;
  onDrop?: (dispatch: DispatcherDispatchRecord) => void;
  onDropAddRoute?: () => void;
  onAddRoute?: () => void;
  onDragstart?: (order: DispatcherOrderRecord) => void;
  onDragmove?: (order: DispatcherOrderRecord, event: DragEvent) => void;
  onDragend?: (order: DispatcherOrderRecord) => void;
  routeLockActive?: boolean;
};

export const DispatcherRouteColumns: React.FC<DispatcherRouteColumnsProps> = ({
  layout,
  resources,
  region,
  date,
  mode = 'viewing',
  fieldShiftAssignments,
  onRemove,
  onDrop,
  onDropAddRoute,
  onAddRoute,
  onDragstart,
  onDragmove,
  onDragend,
  routeLockActive = true,
}) => {
  const validationContext = {
    vehicleTypes: resources.vehicleTypes,
    users: resources.users,
    fieldShiftAssignments,
  };

  const dropEnabled = mode === 'modifying';
  const activeDispatches = layout.dispatches.filter((dispatch) => !dispatch._destroy);

  const handleAddRouteClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      onAddRoute?.();
    },
    [onAddRoute],
  );

  if (activeDispatches.length === 0 && mode !== 'modifying') {
    return (
      <div className="alert alert-info mar-no" role="status">
        No routes scheduled for this date.
      </div>
    );
  }

  return (
    <>
      {activeDispatches.map((dispatch: DispatcherDispatchRecord, routeIndex) => (
        <div key={dispatch.id ?? dispatch.mapID ?? `route-${routeIndex}`} className="dispatcher-list">
          <DispatcherDropZone
            enabled={dropEnabled && isRouteEditable(dispatch, routeLockActive, mode === 'modifying')}
            onDrop={() => onDrop?.(dispatch)}
            className="dispatcher-dispatch"
          >
            <DispatcherDispatch
              dispatch={dispatch}
              region={region}
              date={date}
              depots={resources.depots}
              warehouses={resources.warehouses}
              fieldShiftAssignments={fieldShiftAssignments}
              vehicleTypes={resources.vehicleTypes}
              users={resources.users}
              filters={{ region_id: region.id, date }}
              mode={mode}
              routeIndex={routeIndex}
              color={ALL_MAP_COLORS[routeIndex % ALL_MAP_COLORS.length]}
              dispatchHasWarehouseOrder={dispatchHasWarehouseOrder(dispatch)}
              moverCountErrors={moverCountErrors(dispatch, validationContext)}
              vehicleRequestIconStatus={vehicleRequestIconStatus(dispatch, validationContext)}
              vehicleRequestErrors={vehicleRequestErrorMessages(dispatch, validationContext)}
              onRemove={onRemove ?? (() => undefined)}
              onDragstart={onDragstart ?? (() => undefined)}
              onDragmove={onDragmove ?? (() => undefined)}
              onDragend={onDragend ?? (() => undefined)}
              routeLockActive={routeLockActive}
            />
          </DispatcherDropZone>
        </div>
      ))}

      {mode === 'modifying' && (
        <div className="dispatcher-list">
          <DispatcherDropZone enabled={dropEnabled} onDrop={() => onDropAddRoute?.()} className="dispatcher-dispatch">
            <div className="dispatcher-metadata">
              <div className="dispatcher-title">+</div>
              <div className="dispatcher-buttons">
                <a href="#" className="btn btn-default" onClick={handleAddRouteClick}>
                  Add
                </a>
              </div>
            </div>
            <div className="dispatcher-calendar" />
          </DispatcherDropZone>
        </div>
      )}
    </>
  );
};
